import { ChipXs } from '@app/components/ChipXs';
import { LTime } from '@app/components/date/LTime';
import { TopicEntities } from '@app/modules/logger/screens/stream/features/live-topics/entities/TopicEntities';
import { getTopicDisplayKeyPoints } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-key-points';
import { getTopicDisplaySummary } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-summary';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { TopicCategoryChip } from '@app/modules/logger/screens/stream/features/live-topics/shared/topic-category-chip/TopicCategoryChip';
import { nlpIntegration } from '@mi-front/api-clients';
import { toDateTimeStr } from '@mi-front/core';
import { DurationForRange, LocaleRelativeTime } from '@mi-front/ui';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { LiveTopicActions } from './LiveTopicActions';

const panelItemDevelopingChipMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.itemDeveloping',
  defaultMessage: 'En desarrollo',
});

/**
 * Detail view body for the Live Topics panel: first row has category chip and optional "En desarrollo" chip (left) and Ir + prev/next actions (right); scrollable content (title, date/duration, summary, entities) below.
 * When topic.is_open, an "En desarrollo" (Developing) chip is shown. When isActive, the category chip uses filled variant and a play outline icon is shown next to it (flashing).
 */
export function LiveTopicsDetailView({
  topic,
  topics,
  onSelectTopic,
  isActive = false,
}: {
  topic: nlpIntegration.TopicOutDto;
  topics: nlpIntegration.TopicOutDto[];
  onSelectTopic: (id: string) => void;
  onClose?: () => void;
  /** True when playback playhead is within this topic's time range. */
  isActive?: boolean;
}) {
  const { $t } = useIntl();
  const keyPoints = getTopicDisplayKeyPoints(topic);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 1.5 }}>
        {/* Category + play icon (left) and actions (right) in one row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            {topic.category ? (
              <TopicCategoryChip
                category={topic.category}
                size="medium"
                variant={isActive ? 'filled' : 'outlined'}
              />
            ) : null}
          </Stack>
          <LiveTopicActions topic={topic} topics={topics} onSelectTopic={onSelectTopic} />
        </Stack>

        {/* Title in big strong font */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            mb: 2,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {getTopicDisplayTitle(topic)}
        </Typography>

        {/* Date time - duration */}
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ mb: 1.5, '&::first-letter': { textTransform: 'uppercase' } }}
        >
          <LocaleRelativeTime value={topic.start_time} />
          {' · '}
          <LTime value={toDateTimeStr(new Date(topic.start_time))} hour12={false} showSeconds />
          {/* locale-unextracted-ignore-next-line */}
          {' · '}
          <DurationForRange from={topic.start_time} to={topic.end_time} />

          {topic.is_open && (
            <>
              {' · '}
              <ChipXs
                label={$t(panelItemDevelopingChipMessage)}
                color="default"
                variant="outlined"
              />
            </>
          )}
        </Typography>

        {/* Key points list */}
        {!!keyPoints.length && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage
                id="logger.streamScreen.liveTopics.panel.keyPoints"
                defaultMessage="Puntos clave"
              />
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                pl: 2,
                py: 0,
                mb: 1.5,
                lineHeight: 1.5,
                '& li': {
                  mb: 0.25,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                },
              }}
            >
              {keyPoints.map((point, index) => (
                <Typography key={index} component="li" variant="body1" color="text.primary">
                  {point}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* <TopicMedia topic={topic} /> */}

        {/* Summary body text */}
        {getTopicDisplaySummary(topic) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              lineHeight: 1.5,
            }}
          >
            {getTopicDisplaySummary(topic)}
          </Typography>
        )}

        {/* Entities (same as popup) */}
        {topic.entities && (
          <Box sx={{ mt: 3 }}>
            <TopicEntities
              entities={topic.entities}
              displayMode="short"
              size="medium"
              maxPerType={3}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
